import type { SessionStatus } from "@mux/sdk/v2/client"
import { createSignal } from "solid-js"
import { reconcile, type SetStoreFunction } from "solid-js/store"
import type { GlobalStore } from "./types"
import { createSessionAbortStatusGuard } from "./abort-status"
import { createSessionSnapshotLoader } from "./status-snapshot"

export function createSessionStatusController(input: {
  setStore: SetStoreFunction<GlobalStore>
  load: () => Promise<Record<string, SessionStatus>>
  connected: () => boolean
}) {
  let refresh: () => Promise<boolean> = async () => false
  const [abortRevision, setAbortRevision] = createSignal(0)
  const abort = createSessionAbortStatusGuard({
    onTimeout: () => {
      setAbortRevision((revision) => revision + 1)
      void refresh().catch(() => {})
    },
  })
  const snapshots = createSessionSnapshotLoader({
    load: input.load,
    apply(statuses) {
      input.setStore("session_status", reconcile(abort.overlay(statuses)))
    },
  })
  const refreshSnapshot = snapshots.refresh
  refresh = () => input.connected() ? refreshSnapshot() : Promise.resolve(false)

  return {
    get ready() {
      return snapshots.loaded
    },
    /** A machine snapshot has replaced the statuses this page started with, which may be a cache's. */
    get confirmed() {
      return snapshots.confirmed
    },
    refresh,
    refreshWith<T>(
      load: () => Promise<T>,
      statusesOf: (value: T) => Record<string, SessionStatus>,
      apply: (value: T, statuses: Record<string, SessionStatus>) => void,
    ) {
      const refresh = snapshots.refreshWith(load, statusesOf, apply)
      return () => input.connected() ? refresh() : Promise.resolve(false)
    },
    apply(statuses: Record<string, SessionStatus>) {
      input.setStore("session_status", reconcile(abort.overlay(statuses)))
    },
    overlay: abort.overlay,
    accept(sessionID: string, status: SessionStatus) {
      const wasAborting = abort.active(sessionID)
      const accepted = abort.accept(sessionID, status)
      if (wasAborting && !abort.active(sessionID)) setAbortRevision((revision) => revision + 1)
      return accepted
    },
    changed: snapshots.changed,
    settle(sessionID: string, status: SessionStatus) {
      const wasAborting = abort.active(sessionID)
      abort.settle(sessionID, status)
      if (wasAborting && !abort.active(sessionID)) setAbortRevision((revision) => revision + 1)
    },
    aborting(sessionID: string) {
      abortRevision()
      return abort.active(sessionID)
    },
    beginAbort(sessionID: string) {
      abort.begin(sessionID)
      setAbortRevision((revision) => revision + 1)
    },
    dispose() {
      abort.dispose()
    },
  }
}
